import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AdminFilters.css';

const AdminFilters = ({
  searchTerm,
  onSearchChange,
  registrationDateRange,
  onDateRangeChange,
  registrationSort,
  activitySort,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [draftRange, setDraftRange] = useState({ from: null, to: null });
  const calendarRef = useRef(null);

  const normalizeDate = (value) => {
    if (!value) return '';
    try {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  const toDate = (value) => {
    if (!value) return null;
    try {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    } catch (e) {
      return null;
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isInRange = (date, from, to) => {
    if (!from && !to) return false;
    if (!from) return date.getTime() === to.getTime();
    if (!to) return date.getTime() === from.getTime();
    const time = date.getTime();
    return time >= from.getTime() && time <= to.getTime();
  };

  const isRangeStart = (date, from, to) => {
    if (!from) return false;
    return date.getTime() === from.getTime();
  };

  const isRangeEnd = (date, from, to) => {
    if (!to) return false;
    return date.getTime() === to.getTime();
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleFilterChange = (value) => {
    switch (value) {
      case 'registration_desc':
        onSortChange('newest', 'none');
        break;
      case 'registration_asc':
        onSortChange('oldest', 'none');
        break;
      case 'activity_desc':
        onSortChange('none', 'recent');
        break;
      case 'activity_asc':
        onSortChange('none', 'oldest');
        break;
      default:
        onSortChange('none', 'none');
    }
  };

  const currentSortValue = () => {
    if (registrationSort === 'newest') return 'registration_desc';
    if (registrationSort === 'oldest') return 'registration_asc';
    if (activitySort === 'recent') return 'activity_desc';
    if (activitySort === 'oldest') return 'activity_asc';
    return 'none';
  };

  const openCalendar = () => {
    const from = toDate(registrationDateRange.start);
    const to = toDate(registrationDateRange.end);
    setDraftRange({ from, to });
    setCalendarOpen(true);
  };

  const closeCalendar = () => {
    setCalendarOpen(false);
  };

  const handleDayClick = (date) => {
    if (!date) return;
    
    const clickedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const { from, to } = draftRange;
    
    if (!from || (from && to)) {
      setDraftRange({ from: clickedDate, to: null });
    } else if (from && !to) {
      const fromDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      
      if (clickedDate < fromDate) {
        setDraftRange({ from: clickedDate, to: fromDate });
      } else {
        setDraftRange({ from: fromDate, to: clickedDate });
      }
    }
  };

  const applyRange = () => {
    const { from, to } = draftRange;
    if (from) {
      const start = normalizeDate(from);
      const end = normalizeDate(to || from);
      onDateRangeChange({ start, end });
    }
    closeCalendar();
  };

  const clearRange = () => {
    setDraftRange({ from: null, to: null });
    onDateRangeChange({ start: '', end: '' });
    closeCalendar();
  };

  const getPeriodDisplay = () => {
    if (registrationDateRange.start) {
      if (registrationDateRange.end && registrationDateRange.end !== registrationDateRange.start) {
        return `${registrationDateRange.start} — ${registrationDateRange.end}`;
      }
      return registrationDateRange.start;
    }
    return t('admin.filters.selectPeriod');
  };

  const [currentMonth, setCurrentMonth] = useState(new Date());

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const toggleButtonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target)
      ) {
        closeCalendar();
      }
    };

    if (calendarOpen && toggleButtonRef.current) {
      const buttonRect = toggleButtonRef.current.getBoundingClientRect();
      const calendarWidth = 320;
      const calendarHeight = 400;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = buttonRect.left;
      let top = buttonRect.bottom + 8;
      if (left + calendarWidth > viewportWidth) {
        left = viewportWidth - calendarWidth - 16;
      }
      if (top + calendarHeight > viewportHeight) {
        top = buttonRect.top - calendarHeight - 8;
      }
      if (left < 16) {
        left = 16;
      }
      if (top < 16) {
        top = 16;
      }

      setCalendarPosition({ top, left });

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [calendarOpen]);

  const weekDays = t('admin.filters.weekDays', { returnObjects: true }) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthNames = t('admin.filters.monthNames', { returnObjects: true }) || [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);
  const { from, to } = draftRange;

  return (
    <div className="admin-filters">
      <div className="filters-row">
        <div className="search-filter">
          <input
            type="text"
            placeholder={t('admin.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input-filter"
          />
        </div>

        <div className="filters-group">
          <div className="filter-select-wrapper">
            <label htmlFor="combined-filter">{t('admin.filters.sort')}:</label>
            <select
              id="combined-filter"
              value={currentSortValue()}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="filter-select"
            >
              <option value="none">{t('admin.filters.noSort')}</option>
              <option value="registration_desc">{t('admin.filters.registrationDesc')}</option>
              <option value="registration_asc">{t('admin.filters.registrationAsc')}</option>
              <option value="activity_desc">{t('admin.filters.activityDesc')}</option>
              <option value="activity_asc">{t('admin.filters.activityAsc')}</option>
            </select>
          </div>

          <div className="date-range-filter">
            <label>{t('admin.filters.registrationPeriod')}:</label>
            <button
              type="button"
              ref={toggleButtonRef}
              className="date-picker-toggle"
              onClick={openCalendar}
            >
              {getPeriodDisplay()}
            </button>
            {registrationDateRange.start && (
              <button
                type="button"
                className="clear-filter-btn"
                onClick={clearRange}
                title={t('admin.filters.clearPeriod')}
              >
                ✕
              </button>
            )}

            {calendarOpen && (
              <div
                ref={calendarRef}
                className="calendar-popup"
                style={{
                  top: `${calendarPosition.top}px`,
                  left: `${calendarPosition.left}px`,
                }}
              >
                <div className="calendar-header">
                  <button type="button" className="calendar-nav-btn" onClick={prevMonth}>
                    ‹
                  </button>
                  <div className="calendar-month-year">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </div>
                  <button type="button" className="calendar-nav-btn" onClick={nextMonth}>
                    ›
                  </button>
                </div>

                <div className="calendar-weekdays">
                  {weekDays.map((day, idx) => (
                    <div key={idx} className="calendar-weekday">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="calendar-days">
                  {days.map((date, idx) => {
                    if (!date) {
                      return <div key={idx} className="calendar-day empty" />;
                    }

                    const inRange = isInRange(date, from, to);
                    const isStart = isRangeStart(date, from, to);
                    const isEnd = isRangeEnd(date, from, to);
                    const today = isToday(date);

                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`calendar-day ${inRange ? 'in-range' : ''} ${isStart ? 'range-start' : ''} ${isEnd ? 'range-end' : ''} ${today ? 'today' : ''}`}
                        onClick={() => handleDayClick(date)}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="calendar-footer">
                  {from && (
                    <div className="calendar-selected-info">
                      {normalizeDate(from)}
                      {to && ` — ${normalizeDate(to)}`}
                    </div>
                  )}
                  <div className="calendar-actions">
                    <button
                      type="button"
                      className="apply-filter-btn"
                      onClick={applyRange}
                      disabled={!from}
                    >
                      {t('admin.filters.apply')}
                    </button>
                    <button
                      type="button"
                      className="clear-filter-btn-small"
                      onClick={clearRange}
                    >
                      {t('admin.filters.clear')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFilters;
